// Global Instructions Rule Applied!
//
// Tranches grid orchestration — clone of useAssetTransactionsData (same
// preference persistence, filter-options and fetch-on-change lifecycle)
// against the tranches slice + /tranches/filter endpoints.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  setTranches,
  appendTranches,
  setTranchesActiveFilters,
  setTranchesActiveSorting
} from '../../../state_manager/state_update_actions/tranches/index.ts'
import { fetchTranchesFiltered, fetchTranchesFilterOptions } from '../services/tranchesService.ts'
import { useMenuItemUserPreference } from '../../global_configs/useMenuItemUserPreference.ts'
import {
  mergeUserPreferenceRowData,
  removeUserPreferenceForMenuItem
} from '../../../state_manager/state_update_actions/global_configs/actions.ts'
import {
  areFilterValuesEqual,
  areSortConfigsEqual,
  FilterValue,
  SortConfig,
  PREF_TABLE_COLUMN_KEYS,
  PREF_TABLE_FILTERS,
  PREF_TABLE_SORTING,
  resolveTableFiltersFromPreferenceRow,
  resolveTableSortingFromPreferenceRow
} from '../../global_configs/tableViewPreferences.ts'
import { useReportingDefaultSorting } from '../../global_configs/useReportingDefaultSorting.ts'
import { MENU_ITEM_KEY_TRANCHES } from '../../../state_manager/globals.ts'
import { fetchGlobalConfigsThunk } from '../../global_configs/controller.ts'
import { useTranchesReportId } from '../../../state_manager/hooks/useTranchesReportId.ts'

const DEFAULT_TRANCHES_FILTERS: FilterValue[] = []

export const useTranchesData = () => {
  const dispatch = useDispatch<AppDispatch>()

  const { menuItemId, preferenceRow, persistPreference, deletePreference } =
    useMenuItemUserPreference(MENU_ITEM_KEY_TRANCHES)

  const reportId = useTranchesReportId()
  const defaultTableSorting = useReportingDefaultSorting(reportId)

  const [dataLoading, setDataLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [prefsReady, setPrefsReady] = useState(false)
  const [filterOptions, setFilterOptions] = useState<Record<string, Array<{ value: string; label: string }>>>({})
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false)
  const skipPersistRef = useRef(true)

  const data = useSelector((state: any) => state.tranches?.data ?? [])
  const activeFilters = useSelector((state: any) => state.tranches?.activeFilters ?? [])
  const activeSorting = useSelector((state: any) => state.tranches?.activeSorting ?? [])
  const hasMore = useSelector((state: any) => state.tranches?.hasMore ?? false)
  const currentPage = useSelector((state: any) => state.tranches?.currentPage ?? 1)
  const menuItems = useSelector((state: any) => state.globalConfigs?.menu_items ?? [])

  // Resolve user preferences once globalConfigs has loaded.
  useEffect(() => {
    if (menuItems.length === 0) return
    if (!menuItemId) {
      dispatch(setTranchesActiveFilters({ filters: DEFAULT_TRANCHES_FILTERS }))
      dispatch(setTranchesActiveSorting({ sorting: defaultTableSorting }))
      setPrefsReady(true)
      return
    }
    const filters = resolveTableFiltersFromPreferenceRow(preferenceRow, DEFAULT_TRANCHES_FILTERS)
    const sorting = resolveTableSortingFromPreferenceRow(preferenceRow, defaultTableSorting)
    if (!areFilterValuesEqual(filters, activeFilters)) {
      dispatch(setTranchesActiveFilters({ filters }))
    }
    if (!areSortConfigsEqual(sorting, activeSorting)) {
      dispatch(setTranchesActiveSorting({ sorting }))
    }
    setPrefsReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItemId, menuItems.length, preferenceRow, defaultTableSorting, dispatch])

  const handleFetch = useCallback(
    async (sorting: SortConfig[] = [], filters?: FilterValue[]) => {
      try {
        setDataLoading(true)
        const token = getAccessToken(store.getState().auth)
        const response = await fetchTranchesFiltered(sorting, filters ?? [], 1, null, token)
        dispatch(setTranches({ response }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('Error fetching tranches:', e)
      } finally {
        setDataLoading(false)
      }
    },
    [dispatch]
  )

  const handleSetFilters = useCallback(
    (filters: FilterValue[]) => {
      if (areFilterValuesEqual(filters, activeFilters)) return
      dispatch(setTranchesActiveFilters({ filters }))
    },
    [dispatch, activeFilters]
  )

  const handleSetSorting = useCallback(
    (sorting: SortConfig[]) => {
      if (areSortConfigsEqual(sorting, activeSorting)) return
      dispatch(setTranchesActiveSorting({ sorting }))
    },
    [dispatch, activeSorting]
  )

  const handleResetFilters = useCallback(() => {
    if (!menuItemId) {
      dispatch(setTranchesActiveFilters({ filters: DEFAULT_TRANCHES_FILTERS }))
      return
    }
    void (async () => {
      const result = await deletePreference()
      if (!result.success) return
      dispatch(removeUserPreferenceForMenuItem(menuItemId))
      skipPersistRef.current = true
      dispatch(setTranchesActiveFilters({ filters: DEFAULT_TRANCHES_FILTERS }))
      dispatch(setTranchesActiveSorting({ sorting: defaultTableSorting }))
    })()
  }, [dispatch, menuItemId, deletePreference, defaultTableSorting])

  const persistMergedPreference = useCallback(
    async (extra: Record<string, unknown> = {}) => {
      if (!menuItemId) return { success: false as const, error: 'No menu item' }
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const hadExistingRow = reports.some((p: any) => p.menu_item_id === menuItemId)
      const row = reports.find((p: any) => p.menu_item_id === menuItemId)
      const merged: Record<string, unknown> = {
        ...(row?.data ?? {}),
        ...extra,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        if (hadExistingRow) {
          dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        } else {
          const user = store.getState().auth.user
          if (user) {
            await fetchGlobalConfigsThunk(user)(dispatch)
          }
        }
      }
      return result
    },
    [menuItemId, activeFilters, activeSorting, persistPreference, dispatch]
  )

  const persistColumnFieldOrder = useCallback(
    async (fieldNames: string[]) => persistMergedPreference({ [PREF_TABLE_COLUMN_KEYS]: fieldNames }),
    [persistMergedPreference]
  )

  const resetColumnLayoutToDefault = useCallback(async () => {
    if (!menuItemId) return { success: false as const, error: 'No menu item' }
    const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
    const hadExistingRow = reports.some((p: any) => p.menu_item_id === menuItemId)
    const row = reports.find((p: any) => p.menu_item_id === menuItemId)
    const base: Record<string, unknown> = { ...(row?.data ?? {}) }
    delete base[PREF_TABLE_COLUMN_KEYS]
    const merged: Record<string, unknown> = {
      ...base,
      [PREF_TABLE_FILTERS]: activeFilters,
      [PREF_TABLE_SORTING]: activeSorting
    }
    const result = await persistPreference(undefined, merged)
    if (result.success) {
      if (hadExistingRow) {
        dispatch(mergeUserPreferenceRowData(menuItemId, merged))
      } else {
        const user = store.getState().auth.user
        if (user) {
          await fetchGlobalConfigsThunk(user)(dispatch)
        }
      }
    }
    return result
  }, [menuItemId, activeFilters, activeSorting, persistPreference, dispatch])

  const handleLoadMore = useCallback(
    async (page: number, pageSize: number) => {
      if (loadingMore || !hasMore) return
      try {
        setLoadingMore(true)
        const token = getAccessToken(store.getState().auth)
        const response = await fetchTranchesFiltered(activeSorting, activeFilters, page, pageSize, token)
        dispatch(appendTranches({ response }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('Error loading more tranches:', e)
      } finally {
        setLoadingMore(false)
      }
    },
    [dispatch, activeFilters, activeSorting, hasMore, loadingMore]
  )

  // Filter options once prefs (and therefore auth) are ready.
  useEffect(() => {
    if (!prefsReady) return
    const token = getAccessToken(store.getState().auth)
    if (!token) return
    let cancelled = false
    setFilterOptionsLoading(true)
    ;(async () => {
      const res = await fetchTranchesFilterOptions(token)
      if (!cancelled) {
        setFilterOptions((res.data || {}) as Record<string, Array<{ value: string; label: string }>>)
        setFilterOptionsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [prefsReady])

  // Refetch whenever filters/sorting change (after prefs are ready).
  useEffect(() => {
    if (!prefsReady) return
    const token = getAccessToken(store.getState().auth)
    if (token) {
      handleFetch(activeSorting, activeFilters)
    }
  }, [prefsReady, activeFilters, activeSorting, handleFetch])

  // Persist filter/sort changes (skip the first render — it hydrated from prefs).
  useEffect(() => {
    if (!prefsReady || !menuItemId) return
    if (skipPersistRef.current) {
      skipPersistRef.current = false
      return
    }
    void persistMergedPreference().catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to persist tranches table preferences:', err)
      }
    })
  }, [prefsReady, menuItemId, activeFilters, activeSorting, persistMergedPreference])

  return {
    data,
    dataLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    filterOptions,
    filterOptionsLoading,
    handleFetch,
    refetch: () => handleFetch(activeSorting, activeFilters),
    handleSetFilters,
    handleSetSorting,
    handleResetFilters,
    handleLoadMore,
    defaultTableFilters: DEFAULT_TRANCHES_FILTERS,
    defaultTableSorting,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault
  }
}
